import { Blockquote } from '@radix-ui/themes';
import { CommonFormGrid } from 'components/form/grid';
import { FormHeading } from 'components/form/heading';
import { FormSelect } from 'components/form/select';
import { FormTextField } from 'components/form/textfield';
import { EB } from 'components/layout/error-boundary';
import { FormContext } from 'contexts/form';
import { InvestorType } from 'lib/enums/investor';
import { InvestorDoc } from 'lib/interfaces/database/investor';
import {
  InvestorPrivateDoc,
  ManageInvestorPayload,
} from 'lib/interfaces/database/investor-private';
import { StringUtil } from 'lib/utilities/string';
import _ from 'lodash';
import { useContext, useState } from 'react';

export const InvestorForm = () => {
  const { data, setData } = useContext(FormContext);

  const [defaultData] = useState<ManageInvestorPayload>(data);
  const { investor, investorPrivate } = defaultData;

  const typedData: ManageInvestorPayload = data;

  const setInvestor = (v: Partial<InvestorDoc>) => {
    const payload: ManageInvestorPayload = _.clone(typedData);
    payload.investor = { ...payload.investor, ...v };
    setData(payload);
  };

  const setInvestorPrivate = (v: Partial<InvestorPrivateDoc>) => {
    const payload: ManageInvestorPayload = _.clone(typedData);
    payload.investorPrivate = { ...payload.investorPrivate, ...v };
    setData(payload);
  };

  return (
    <EB>
      <CommonFormGrid columns="3">
        <FormHeading label="Investor" gridColumn="span 3" />
        <FormTextField
          label="Code"
          defaultValue={investor.code}
          onChange={(v) =>
            setInvestor({
              code: v,
            })
          }
          required
        />

        <FormTextField
          label="Onboarded Date"
          defaultValue={StringUtil.printDate(investor.onboarded)}
          onDateChange={(v) =>
            setInvestor({
              onboarded: v,
            })
          }
        />

        <FormSelect
          label="Ongoing Relationship"
          defaultValue={investor.ongoing?.toString()}
          options={[
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
          ]}
          onBooleanChange={(v) =>
            setInvestor({
              ongoing: v,
            })
          }
          required
        />
      </CommonFormGrid>

      <Blockquote mt="4" mb="4">
        TODO: Referral support
      </Blockquote>

      <CommonFormGrid columns="2">
        <FormHeading label="Investor Details" gridColumn="span 2" />

        <FormSelect
          label="Type"
          defaultValue={investorPrivate.type}
          options={Object.values(InvestorType).map((o) => ({
            label: o,
            value: o,
          }))}
          onOptionalChange={(v) =>
            setInvestorPrivate({
              type: v as InvestorType,
            })
          }
        />

        {typedData.investorPrivate.type === InvestorType.Institution && (
          <FormTextField
            gridColumnStart="1"
            label="Entity Name"
            defaultValue={investorPrivate.entityName}
            onChange={(v) =>
              setInvestorPrivate({
                entityName: v,
              })
            }
          />
        )}

        {typedData.investorPrivate.type === InvestorType.Individual && (
          <>
            <FormTextField
              gridColumnStart="1"
              label="First Name"
              defaultValue={investorPrivate.firstName}
              onChange={(v) =>
                setInvestorPrivate({
                  firstName: v,
                })
              }
            />

            <FormTextField
              label="Last Name"
              defaultValue={investorPrivate.lastName}
              onChange={(v) =>
                setInvestorPrivate({
                  lastName: v,
                })
              }
            />
          </>
        )}

        <FormTextField
          gridColumnStart="1"
          label="Address Line 1"
          defaultValue={investorPrivate.address1}
          onChange={(v) =>
            setInvestorPrivate({
              address1: v,
            })
          }
          required
        />

        <FormTextField
          label="Address Line 2"
          defaultValue={investorPrivate.address2}
          onChange={(v) =>
            setInvestorPrivate({
              address2: v,
            })
          }
        />

        <FormTextField
          label="City"
          defaultValue={investorPrivate.city}
          onChange={(v) =>
            setInvestorPrivate({
              city: v,
            })
          }
          required
        />

        <FormTextField
          label="Province"
          defaultValue={investorPrivate.province}
          onChange={(v) =>
            setInvestorPrivate({
              province: v,
            })
          }
          required
        />

        <FormTextField
          label="Postal Code"
          defaultValue={investorPrivate.postal}
          onChange={(v) =>
            setInvestorPrivate({
              postal: v,
            })
          }
          required
        />

        <FormTextField
          label="Country"
          defaultValue={investorPrivate.country}
          onChange={(v) =>
            setInvestorPrivate({
              country: v,
            })
          }
          required
        />
      </CommonFormGrid>
    </EB>
  );
};
